import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useAuth } from "../app/auth/hooks";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  TicketStatus,
  TicketViewListOptions,
} from "@shared-core/data/adapter";
import { notifyTicketsChanged } from "@shared-core/domain/ticketEvents";
import { ticketListColumns } from "@shared-core/domain/ticket-list-columns";
import type * as API from "@shared-core/data/api-types";
import {
  getApiBaseUrl,
  joinApiUrl,
  getAuthToken,
} from "@shared-core/data/api/client";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { TicketCard } from "../app/components/domain/tickets/TicketCard";
import { TicketModal } from "../app/components/domain/tickets/TicketModal";
import { CustomerScopeChip } from "../app/components/domain/customers/CustomerScopeChip";
import { Button } from "../app/components/primitives/button";
import { Input } from "../app/components/primitives/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../app/components/primitives/collapsible";
import { SlideOverPanel } from "../app/components/patterns/SlideOverPanel";
import { FilterCheckboxGroup } from "../app/components/patterns/FilterCheckboxGroup";
import {
  Plus,
  ArrowUpDown,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Megaphone,
  Download,
} from "lucide-react";

// Every subject_type that belongs in the pink box — marketing work, pinned
// to the top of this page so it doesn't get lost under the much
// higher-volume account_health (at-risk) auto-tickets.
//
// Two sources, not one. The campaign types come from
// marketing_campaigns_module: campaign_contact (one ticket per contact per
// campaign), campaign_bounce, and the older campaign / campaign_engaged
// tickets it replaced, which stay until worked. "landing_page_lead"
// comes from marketing_leads_module — someone filled in a landing-page form
// — and is marketing work by the same argument the box was built on: a
// fresh enquiry is the hottest thing on this page and had been arriving
// with *less* prominence than a campaign email open.
//
// Anything added here must also get an entry in
// CAMPAIGN_TICKET_TYPE_OPTIONS below. The main list excludes everything in
// this array, and the box normally fetches only the selected toggle — so a
// type listed here with no toggle is excluded from one place and unreachable
// in the other, i.e. invisible except via search.
const CAMPAIGN_TICKET_SUBJECT_TYPES = [
  "campaign_contact",
  "campaign",
  "campaign_engaged",
  "campaign_bounce",
  "landing_page_lead",
];

// A real Previous/Next pager (see the campaignPage state below), not a
// scroll container — 25 keeps each page a similar size to the main list's
// own PAGE_SIZE and avoids the "am I scrolling the page or the box?"
// confusion a tall scrollable box caused.
const CAMPAIGN_TICKETS_PAGE_SIZE = 25;

// Reps prioritise these ticket types very differently (a campaign contact is
// worth calling now; a bounce means finding a new contact) — the toggle lets
// them focus on one at a time instead of always seeing both interleaved.
// "campaign" (call-step/activation tickets) isn't one of these and is
// reached only via the campaign filter dropdown, not this toggle. No "All"
// option for manual browsing — a campaign contact is the highest-priority
// queue, so the box loads straight into it rather than the interleaved
// everything-view. A search query is a different intent (a rep looking for
// one specific record they already know about) and bypasses this toggle
// entirely — see fetchCampaignTicketData/fetchActionedCampaignTicketData.
//
// There is deliberately no "never opened" option — that ticket type was
// removed 2026-09 (low-signal, and it compounded the repeat-contact ticket
// volume problem — see
// docs/features/campaign-projects/09_repeat_contact_ticket_fix_plan.md).
type CampaignTicketTypeFilter =
  "campaign_contact" | "campaign_bounce" | "landing_page_lead";

// The subject_types each toggle lists. "Campaign" is the one ticket per
// contact per campaign (campaign_contact), and also lists the older
// campaign_engaged tickets it replaced while any are still open.
const CAMPAIGN_TICKET_TYPE_SUBJECT_TYPES: Record<
  CampaignTicketTypeFilter,
  string[]
> = {
  campaign_contact: ["campaign_contact", "campaign_engaged"],
  campaign_bounce: ["campaign_bounce"],
  landing_page_lead: ["landing_page_lead"],
};

// ?campaign_type= as a toggle; links saved before the "Campaign" toggle
// replaced "Engaged" say campaign_engaged.
function parseCampaignTicketTypeFilter(
  value: string | null,
): CampaignTicketTypeFilter {
  return value === "campaign_bounce" || value === "landing_page_lead"
    ? value
    : "campaign_contact";
}
// "Landing page" is a source where the other three are campaign states, and
// that asymmetry is deliberate: it reuses the name the rest of the system
// already uses (the lead source, the subject_type, the ticket title all say
// "landing page") rather than minting a fresh word for the same thing.
const CAMPAIGN_TICKET_TYPE_OPTIONS: {
  key: CampaignTicketTypeFilter;
  label: string;
  //: Spelled out per option rather than composed from `label`, because
  //: "No " + label + " campaign tickets" reads as "No landing page
  //: campaign tickets" — clumsy, and untrue of a landing-page enquiry.
  emptyNoun: string;
}[] = [
  {
    key: "campaign_contact",
    label: "Campaign",
    emptyNoun: "campaign tickets",
  },
  {
    key: "campaign_bounce",
    label: "Bounced",
    emptyNoun: "bounced campaign tickets",
  },
  {
    key: "landing_page_lead",
    label: "Landing page",
    emptyNoun: "landing-page enquiries",
  },
];

//: True when the selected toggle shows tickets that belong to no campaign.
//: The campaign dropdown ANDs with the toggle, so leaving a campaign
//: selected here would return nothing and read as "there are no enquiries"
//: rather than "these two filters cannot both hold".
function isCampaignlessTicketType(filter: CampaignTicketTypeFilter): boolean {
  return filter === "landing_page_lead";
}

// The pink box's own sort, independent of the main list's `sortBy` — reps
// mostly want to work these newest-first or by priority, not by the main
// list's due-date/title/tag options which don't fit auto-generated
// campaign tickets as well.
type CampaignSortField = "created_at" | "priority";
const CAMPAIGN_SORT_FIELD_TO_PARAM: Record<CampaignSortField, string> = {
  created_at: "-created_at",
  priority: "priority",
};

// TicketsPage follows the canonical list-endpoint pattern (see
// docs/list-endpoints.md): the backend does the filtering, sorting,
// searching, and paginating. The page owns filter/sort/page state
// and refetches whenever any of them change. There is no client-side
// `.filter()` or `.sort()` on the adapter response.
//
// Customer name, primary contact, linked alert/opportunity summaries, and
// notes are delivered inline by GET /views/tickets, eliminating the
// previous bulk getCustomers/getContacts/getAlerts/getOpportunities/getNotes
// calls that caused the pageSize:200 scalability ceiling and the
// silent data loss bug (issue #19 in user-testing-backlog).
//
// Every filter in the panel is a checkbox multi-select: values checked
// within one filter combine with OR (status = open OR waiting), and the
// filters themselves combine with AND — the same semantics
// PatternCurationListQuery uses. An empty selection means "no restriction",
// so the page defaults to showing every ticket until the user checks a box.
// The status menu also needs a live count next to every option, so the page
// always issues five parallel per-status requests for those counts,
// alongside one request for the actual current-filter selection (see
// `currentView`).

type SortField = "due_date" | "created_at" | "title" | "priority" | "tags";
const SORT_FIELD_TO_PARAM: Record<SortField, string> = {
  due_date: "due_date",
  created_at: "-created_at",
  title: "title",
  // Ascending = most severe first (urgent...low) — see the backend's
  // severity-rank sort expression, not alphabetical.
  priority: "priority",
  // Ascending = alphabetically-first tag; tagless tickets sort last.
  tags: "tags",
};

const PAGE_SIZE = 50;

type ActionType = "call" | "email" | "meeting" | "general";
// Who raised the ticket: an automated flow (is_system_generated) or a person.
type TicketSource = "system" | "person";
type Priority = "low" | "normal" | "high" | "urgent";
// "mine" scopes to the signed-in user's assignments (assignedToUserId set);
// "all" drops that filter entirely so every visible ticket shows, regardless
// of assignee — portfolio scoping (allowed_customer_ids) still applies
// server-side, this only toggles the assignee filter.
// "needs_cover" (csr-ticket-routing V2) — the ticket's CSR is absent today
// (per work_visibility_module's daily snapshot) and no covering buddy has
// been assigned yet. A scope, not a filter, so it reads alongside "My
// Tickets"/"All Tickets" as a third place to look rather than a checkbox
// someone has to know to tick.
// "department" (department-ticket-routing) — this user's department
// inbox(es). Only ever offered when the caller belongs to at least one
// department (see myDepartments below) — a user in none must see no change
// at all (frontend update spec §4's acceptance bar), so this scope's tab is
// absent, not merely non-functional, for the overwhelming majority.
type TicketScope = "mine" | "all" | "needs_cover" | "department";

// Five-state lifecycle (spec: docs/features/ticket-system/01_product_brief.md
// §"lifecycle").
const STATUS_OPTIONS: { key: TicketStatus; label: string }[] = [
  { key: "open", label: "Open" },
  { key: "in_progress", label: "In Progress" },
  { key: "waiting", label: "Waiting" },
  { key: "resolved", label: "Resolved" },
  { key: "closed", label: "Closed" },
];

// Closed tickets are noise by default — everything else in the lifecycle
// still needs attention or is worth a quick look. Unlike this page's other
// filters (empty selection = no restriction, see the module comment above),
// status has an opinionated default: nothing checked means "everything
// except closed", not "everything". Checking "Closed" in the filter panel
// (alone or alongside other statuses) is how a user opts back in.
const DEFAULT_VISIBLE_STATUSES: TicketStatus[] = STATUS_OPTIONS.filter(
  (s) => s.key !== "closed",
).map((s) => s.key);

type TicketsByStatus = Record<
  TicketStatus,
  { items: API.TicketViewItem[]; total: number }
>;

const EMPTY_STATUS_MESSAGE: Record<TicketStatus, string> = {
  open: "No open tickets - great work!",
  in_progress: "No tickets in progress",
  waiting: "No tickets waiting",
  resolved: "No resolved tickets",
  closed: "No closed tickets yet",
};

function emptyTicketsByStatus(): TicketsByStatus {
  return {
    open: { items: [], total: 0 },
    in_progress: { items: [], total: 0 },
    waiting: { items: [], total: 0 },
    resolved: { items: [], total: 0 },
    closed: { items: [], total: 0 },
  };
}

export function TicketsPage() {
  const dataAdapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  // "My Tickets" scopes the list to the signed-in user's assignments.
  const { user } = useAuth();
  const currentUserId = user?.id;
  const [searchParams, setSearchParams] = useSearchParams();
  const [isExportingCampaignTickets, setIsExportingCampaignTickets] =
    useState(false);

  // Initialise customer filter from URL param so deep-links from customer
  // detail land with the correct scope applied from the first load.
  const urlCustomer = searchParams.get("customer") ?? undefined;

  // Filter/sort/page state is mirrored to the URL (see the sync effect
  // below) so that navigating to a ticket and back restores exactly where
  // you were, rather than remounting to defaults — filters live in React
  // state only otherwise, and a route change to /tickets/:id unmounts this
  // page. Read the initial value from the URL once, at mount.
  const csvParam = (key: string): string[] =>
    searchParams.get(key)?.split(",").filter(Boolean) ?? [];
  const intParam = (key: string, fallback: number): number => {
    const raw = searchParams.get(key);
    const parsed = raw ? Number.parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };

  const [ticketsByStatus, setTicketsByStatus] = useState<TicketsByStatus>(
    emptyTicketsByStatus(),
  );
  const [loading, setLoading] = useState(true);
  // Distinct from "empty" — a failed fetch must say so rather than quietly
  // showing whatever the previous (possibly empty) state happened to be.
  const [loadError, setLoadError] = useState(false);
  const [filterStatus, setFilterStatus] = useState<TicketStatus[]>(
    () => csvParam("status") as TicketStatus[],
  );
  // The list actually rendered: every ticket matching the current filter
  // selection (or every ticket, when nothing is checked).
  const [currentView, setCurrentView] = useState<{
    items: API.TicketViewItem[];
    total: number;
  }>({ items: [], total: 0 });

  const [sortBy, setSortBy] = useState<SortField>(
    () => (searchParams.get("sort") as SortField) || "due_date",
  );
  const [scope, setScope] = useState<TicketScope>(
    () => (searchParams.get("scope") as TicketScope) || "mine",
  );
  // department-ticket-routing: departments the signed-in user belongs to —
  // drives both whether the department scope tab shows at all (empty ->
  // absent, per the acceptance bar) and, when there's more than one, the
  // picker that lets a multi-site member switch between their own
  // department inboxes rather than seeing N separate tabs.
  const [myDepartments, setMyDepartments] = useState<API.Department[]>([]);
  const [selectedDepartmentSlug, setSelectedDepartmentSlug] = useState(
    () => searchParams.get("department") || "",
  );
  const [campaignTickets, setCampaignTickets] = useState<{
    items: API.TicketViewItem[];
    total: number;
  }>({ items: [], total: 0 });
  // Untouched-ticket count per Campaign/Bounced/Landing page toggle, for the
  // badge on each — see fetchCampaignTicketData.
  const [campaignTypeCounts, setCampaignTypeCounts] = useState<
    Record<CampaignTicketTypeFilter, number>
  >({ campaign_contact: 0, campaign_bounce: 0, landing_page_lead: 0 });
  // Collapsed by default (unlike the historical default) — a rep should
  // opt into the pink box rather than always seeing it expanded. Restored
  // from the URL so browser back from a ticket detail page returns to
  // whichever state (open/closed) it was left in, same as every other
  // filter on this page.
  const [campaignSectionExpanded, setCampaignSectionExpanded] = useState(
    () => searchParams.get("campaign_open") === "1",
  );
  // Multi-select for the pink box only (scoped there deliberately — see the
  // bulk-close/bulk-reassign section below): a rep working one campaign
  // often finds several engaged contacts at the same company and wants to
  // close all but the one they're actually calling, with one shared note —
  // or a manager reallocating an exported spreadsheet's worth of stalling
  // tickets wants to hand several off to a different rep at once.
  const [selectedCampaignTicketIds, setSelectedCampaignTicketIds] = useState<
    Set<string>
  >(new Set());
  const [bulkCloseNote, setBulkCloseNote] = useState("");
  const [bulkClosing, setBulkClosing] = useState(false);
  const [bulkCloseSkipped, setBulkCloseSkipped] = useState<
    API.BulkCloseTicketsResult["skipped"]
  >([]);
  // Reassign shares the same selection set as bulk-close above — a rep
  // picks tickets once, then chooses either "close with a note" or
  // "reassign to someone else", not both on the same selection.
  const [bulkReassignToUserId, setBulkReassignToUserId] = useState("");
  const [bulkReassigning, setBulkReassigning] = useState(false);
  const [bulkReassignSkipped, setBulkReassignSkipped] = useState<
    API.BulkDelegateTicketsResult["skipped"]
  >([]);
  // Independent from the main list's `loading` — the campaign box refetches
  // on its own trigger set (see fetchCampaignTicketData below) and must be
  // able to show its own scoped loading state without a page-wide spinner.
  const [campaignLoading, setCampaignLoading] = useState(true);
  const [campaignTypeFilter, setCampaignTypeFilter] =
    useState<CampaignTicketTypeFilter>(() =>
      parseCampaignTicketTypeFilter(searchParams.get("campaign_type")),
    );
  // "all" sentinel, matching campaignFilter's convention.
  const [campaignFilter, setCampaignFilter] = useState<string>(
    () => searchParams.get("campaign") || "all",
  );
  const [showUrgentOnly, setShowUrgentOnly] = useState(
    () => searchParams.get("urgent") === "1",
  );
  const [campaignSortBy, setCampaignSortBy] = useState<CampaignSortField>(
    () =>
      (searchParams.get("campaign_sort") as CampaignSortField) || "created_at",
  );
  const [campaignPage, setCampaignPage] = useState(() =>
    intParam("campaign_page", 1),
  );
  // The "Actioned" box — same campaign tickets, split out once a rep has
  // left a human-authored activity row (comment, status change,
  // reassignment, ...) on them. Its own state/page/loading, mirroring the
  // pink "untouched" box above, so the two never fight over one fetch or
  // one page cursor. Collapsed by default (unlike the pink box) — it's a
  // "see what's been worked" reference, not the primary call-to-action.
  const [actionedCampaignTickets, setActionedCampaignTickets] = useState<{
    items: API.TicketViewItem[];
    total: number;
  }>({ items: [], total: 0 });
  const [actionedSectionExpanded, setActionedSectionExpanded] = useState(
    () => searchParams.get("actioned_open") === "1",
  );
  const [actionedCampaignLoading, setActionedCampaignLoading] = useState(true);
  const [actionedCampaignPage, setActionedCampaignPage] = useState(() =>
    intParam("actioned_campaign_page", 1),
  );
  const [availableCampaigns, setAvailableCampaigns] = useState<
    { id: string; name: string }[]
  >([]);
  const [filterActionType, setFilterActionType] = useState<ActionType[]>(
    () => csvParam("action_type") as ActionType[],
  );
  const [filterCustomer, setFilterCustomer] = useState<string[]>(
    urlCustomer ? [urlCustomer] : [],
  );
  const [filterCategory, setFilterCategory] = useState<string[]>(() =>
    csvParam("category"),
  );
  const [filterPriority, setFilterPriority] = useState<Priority[]>(
    () => csvParam("priority") as Priority[],
  );
  const [filterSource, setFilterSource] = useState<TicketSource[]>(
    () => csvParam("source") as TicketSource[],
  );
  // Assignee filter only applies in "All Tickets" scope — "My Tickets"
  // already scopes to the current user via assignedToUserId.
  const [filterAssignee, setFilterAssignee] = useState<string[]>(() =>
    csvParam("assignee"),
  );
  const [categories, setCategories] = useState<API.TicketCategory[]>([]);
  const [assignableUsers, setAssignableUsers] = useState<
    { id: string; name: string }[]
  >([]);

  // Free-text search, debounced so every keystroke doesn't fire a request.
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("q") || "",
  );
  const [searchQuery, setSearchQuery] = useState(
    () => searchParams.get("q") || "",
  );
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Pagination applies to the current filter selection; the five per-status
  // count requests always fetch page 1 so their badge counts stay accurate
  // without paginating statuses the user isn't looking at.
  const [page, setPage] = useState(() => intParam("page", 1));

  // Ticket categories are an admin-curated taxonomy (Settings > Ticket
  // Categories), so the filter dropdown is sourced from that endpoint
  // rather than derived from currently-loaded tickets — a category with
  // no visible tickets yet should still be selectable.
  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .listTicketCategories(true)
      .then((cats) => {
        if (!cancelled) setCategories(cats);
      })
      .catch((error) => {
        console.error("Error loading ticket categories:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  // The staff list (GET /users, active users) — the app's one "who can I
  // pick from" list — for the assignee filter and bulk reassign, same as the
  // ticket modal's reassign picker.
  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .listUsers()
      .then((page) => {
        if (!cancelled) {
          setAssignableUsers(
            page.items.map((u) => ({ id: u.id, name: u.name })),
          );
        }
      })
      .catch((error) => {
        console.error("Error loading assignable users:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  // department-ticket-routing: this user's own department membership —
  // see myDepartments above for why. Empty for the overwhelming majority,
  // by design.
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    dataAdapter
      .getDepartmentsForUser(currentUserId)
      .then((depts) => {
        if (cancelled) return;
        setMyDepartments(depts);
        // Default the picker to the first membership whenever the current
        // selection isn't (or is no longer) one of them — covers both the
        // very first load and a department the user was removed from.
        setSelectedDepartmentSlug((prev) =>
          depts.some((d) => d.slug === prev) ? prev : (depts[0]?.slug ?? ""),
        );
      })
      .catch((error) => {
        console.error("Error loading the user's departments:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter, currentUserId]);

  // Options for the campaign filter dropdown in the pink box — every
  // campaign, not just ones the viewer has tickets for (mirrors
  // assignableUsers above: a reference list fetched once, not derived from
  // currently-loaded tickets).
  useEffect(() => {
    let cancelled = false;
    dataAdapter
      .listMarketingCampaigns({ pageSize: 200 })
      .then((page) => {
        if (!cancelled) {
          setAvailableCampaigns(
            page.items.map((c) => ({ id: c.id, name: c.name })),
          );
        }
      })
      .catch((error) => {
        console.error("Error loading campaigns for filter:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [dataAdapter]);

  // Resolve display name for the active customer scope chip.
  const [customerName, setCustomerName] = useState<string | null>(null);
  useEffect(() => {
    if (!urlCustomer) {
      setCustomerName(null);
      return;
    }
    // Derive name from the first loaded ticket matching the customer.
    const match = STATUS_OPTIONS.flatMap(
      (tab) => ticketsByStatus[tab.key].items,
    ).find((t) => t.customer_id === urlCustomer && t.customer_name);
    if (match?.customer_name) {
      setCustomerName(match.customer_name);
    }
  }, [urlCustomer, ticketsByStatus]);

  // Deep-link from the notification bell: the default "all" status filter
  // already shows every status, so a linked ticket only fails to appear
  // when it belongs to someone else under "My Tickets" scope. Widen to
  // "All Tickets" once before giving up — otherwise the deep link silently
  // fails to resolve for anyone but the assignee.
  const urlTicket = searchParams.get("ticket") ?? undefined;
  const [widenedForTicket, setWidenedForTicket] = useState<string | null>(null);
  useEffect(() => {
    if (!urlTicket) return;
    const existsUnderCurrentScope = STATUS_OPTIONS.some((tab) =>
      ticketsByStatus[tab.key].items.some((item) => item.id === urlTicket),
    );
    if (
      !existsUnderCurrentScope &&
      scope === "mine" &&
      widenedForTicket !== urlTicket
    ) {
      setWidenedForTicket(urlTicket);
      setScope("all");
    }
  }, [urlTicket, ticketsByStatus, scope, widenedForTicket]);

  const [showAddTicket, setShowAddTicket] = useState(false);
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Any change to what's being viewed restarts pagination from page 1. The
  // reset keys on the filters' *values*, not their identities: a mount-time
  // effect handing back a fresh but equal `[]` (or StrictMode re-running
  // effects) would otherwise stomp a page restored from the URL back to 1 —
  // which sent anyone returning from a ticket on page 4 back to page 1.
  // The department only counts in the department view: for a member, the
  // picker's default arrives after mount (the departments fetch), and
  // counting it elsewhere sent them back to page 1 on every return.
  const viewedDepartmentSlug =
    scope === "department" ? selectedDepartmentSlug : "";
  const mainListViewKey = JSON.stringify([
    filterStatus,
    scope,
    viewedDepartmentSlug,
    sortBy,
    filterActionType,
    filterCustomer,
    filterCategory,
    filterPriority,
    filterSource,
    filterAssignee,
    searchQuery,
  ]);
  const lastMainListViewKey = useRef(mainListViewKey);
  useEffect(() => {
    if (lastMainListViewKey.current === mainListViewKey) return;
    lastMainListViewKey.current = mainListViewKey;
    setPage(1);
  }, [mainListViewKey]);

  // The campaign boxes are their own independent lists with their own
  // pagination — reset both (pink and actioned share the same filters, see
  // fetchCampaignTicketData/fetchActionedCampaignTicketData) only on the
  // subset of filters that actually affect them (they ignore action-type/
  // category/priority, see the comment on campaignOptions below — status,
  // assignee, and search all do apply, plus their own filters). Same
  // value-keyed guard as above. Resetting actionedCampaignPage here matters
  // for search specifically: without it, a rep paginated to page 2 of the
  // Actioned box who then searches could auto-expand it onto an empty page
  // 2 of the new, smaller result set — reading as "no match" exactly like
  // the collapsed-box problem this search fix exists to solve.
  const campaignViewKey = JSON.stringify([
    scope,
    viewedDepartmentSlug,
    filterCustomer,
    filterStatus,
    filterAssignee,
    campaignTypeFilter,
    campaignFilter,
    showUrgentOnly,
    searchQuery,
    campaignSortBy,
  ]);
  const lastCampaignViewKey = useRef(campaignViewKey);
  useEffect(() => {
    if (lastCampaignViewKey.current === campaignViewKey) return;
    lastCampaignViewKey.current = campaignViewKey;
    setCampaignPage(1);
    setActionedCampaignPage(1);
  }, [campaignViewKey]);

  // Mirror the filter/sort/page state into the URL (replacing, not pushing,
  // so filter changes don't spam browser history) — this is what lets
  // browser back from the ticket-detail page restore exactly where you
  // were, instead of remounting to defaults.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const setOrDelete = (key: string, value: string | null) => {
      if (value) next.set(key, value);
      else next.delete(key);
    };
    setOrDelete("scope", scope !== "mine" ? scope : null);
    setOrDelete(
      "department",
      scope === "department" && selectedDepartmentSlug
        ? selectedDepartmentSlug
        : null,
    );
    setOrDelete("status", filterStatus.length ? filterStatus.join(",") : null);
    setOrDelete("sort", sortBy !== "due_date" ? sortBy : null);
    setOrDelete("page", page !== 1 ? String(page) : null);
    setOrDelete(
      "action_type",
      filterActionType.length ? filterActionType.join(",") : null,
    );
    setOrDelete(
      "category",
      filterCategory.length ? filterCategory.join(",") : null,
    );
    setOrDelete(
      "priority",
      filterPriority.length ? filterPriority.join(",") : null,
    );
    setOrDelete(
      "assignee",
      filterAssignee.length ? filterAssignee.join(",") : null,
    );
    setOrDelete("source", filterSource.length ? filterSource.join(",") : null);
    setOrDelete("q", searchQuery || null);
    setOrDelete(
      "campaign_type",
      campaignTypeFilter !== "campaign_contact" ? campaignTypeFilter : null,
    );
    setOrDelete("campaign", campaignFilter !== "all" ? campaignFilter : null);
    setOrDelete("urgent", showUrgentOnly ? "1" : null);
    setOrDelete(
      "campaign_sort",
      campaignSortBy !== "created_at" ? campaignSortBy : null,
    );
    setOrDelete(
      "campaign_page",
      campaignPage !== 1 ? String(campaignPage) : null,
    );
    setOrDelete(
      "actioned_campaign_page",
      actionedCampaignPage !== 1 ? String(actionedCampaignPage) : null,
    );
    // Collapse state — no "default" to omit against, since the pink box's
    // and the actioned box's defaults differ (closed/closed); persisting
    // both explicitly is what lets browser back restore whichever state
    // (open or closed) the user left each box in.
    setOrDelete("campaign_open", campaignSectionExpanded ? "1" : null);
    setOrDelete("actioned_open", actionedSectionExpanded ? "1" : null);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reads searchParams to preserve customer/ticket params set elsewhere; including it would loop
  }, [
    scope,
    selectedDepartmentSlug,
    filterStatus,
    sortBy,
    page,
    filterActionType,
    filterCategory,
    filterPriority,
    filterSource,
    filterAssignee,
    searchQuery,
    campaignTypeFilter,
    campaignFilter,
    showUrgentOnly,
    campaignSortBy,
    campaignPage,
    actionedCampaignPage,
    campaignSectionExpanded,
    actionedSectionExpanded,
    setSearchParams,
  ]);

  // The assignee filter only means something in "All Tickets" scope —
  // clear it when switching away so it doesn't linger invisibly (the
  // filter menu hides the control outside "all" scope too).
  useEffect(() => {
    // Keep the existing empty array so a no-op clear doesn't refire every
    // request keyed on the filter's identity.
    if (scope !== "all")
      setFilterAssignee((current) => (current.length ? [] : current));
  }, [scope]);

  // Builds the two requests the main list needs on every refetch: one per
  // real status (page 1 only, purely for the checkbox badge counts) plus one
  // for the actual current filter selection — a single query, since the
  // backend now takes `status` as zero-or-more values directly. Customer,
  // contact, and linked items arrive inline — no separate bulk fetches
  // needed. Deliberately excludes the campaign box's own filters
  // (campaignTypeFilter/campaignFilter/campaignPage) so toggling those
  // doesn't refire these six requests — see fetchCampaignTicketData below.
  const fetchMainTicketData = useCallback(async (): Promise<{
    ticketsByStatus: TicketsByStatus;
    currentView: { items: API.TicketViewItem[]; total: number };
  }> => {
    const baseOptions: TicketViewListOptions = {
      sort: SORT_FIELD_TO_PARAM[sortBy],
      pageSize: PAGE_SIZE,
      // Campaign-raised tickets get their own pinned "Campaign tickets" box
      // below — hide them from the main list so they don't show twice.
      excludeSubjectType: CAMPAIGN_TICKET_SUBJECT_TYPES,
    };
    if (scope === "mine") {
      baseOptions.assignedToUserId = currentUserId
        ? [currentUserId]
        : undefined;
    } else if (scope === "needs_cover") {
      baseOptions.needsCover = true;
    } else if (scope === "department") {
      if (selectedDepartmentSlug)
        baseOptions.department = [selectedDepartmentSlug];
    } else if (filterAssignee.length) {
      baseOptions.assignedToUserId = filterAssignee;
    }
    if (filterActionType.length) baseOptions.actionType = filterActionType;
    if (filterCustomer.length) baseOptions.customerId = filterCustomer;
    if (filterCategory.length) baseOptions.categoryId = filterCategory;
    if (filterPriority.length) baseOptions.priority = filterPriority;
    // Both boxes ticked means every ticket, same as neither.
    if (filterSource.length === 1)
      baseOptions.systemGenerated = filterSource[0] === "system";
    if (searchQuery) baseOptions.q = searchQuery;

    const [statusResponses, currentResponse] = await Promise.all([
      Promise.all(
        STATUS_OPTIONS.map((tab) =>
          dataAdapter.getTicketViews({
            ...baseOptions,
            status: [tab.key],
            page: 1,
          }),
        ),
      ),
      dataAdapter.getTicketViews({
        ...baseOptions,
        status: filterStatus.length ? filterStatus : DEFAULT_VISIBLE_STATUSES,
        page,
      }),
    ]);
    const ticketsByStatus = emptyTicketsByStatus();
    STATUS_OPTIONS.forEach((tab, i) => {
      ticketsByStatus[tab.key] = {
        items: statusResponses[i].items,
        total: statusResponses[i].total,
      };
    });
    return {
      ticketsByStatus,
      currentView: {
        items: currentResponse.items,
        total: currentResponse.total,
      },
    };
  }, [
    dataAdapter,
    sortBy,
    scope,
    selectedDepartmentSlug,
    filterActionType,
    filterCustomer,
    filterCategory,
    filterPriority,
    filterSource,
    filterAssignee,
    searchQuery,
    filterStatus,
    page,
    currentUserId,
  ]);

  // Pinned campaign-ticket section: scoped the same way as the rest of the
  // page (who/which customer), and shares the main list's status and
  // assignee filters too — both are real for campaign tickets (they carry
  // a sales rep and the full ticket lifecycle, including closed). It
  // deliberately still ignores action-type/category (meaningless here:
  // every campaign ticket is action_type "call" and none carry a category
  // by default) and priority (the box already has its own urgent-only
  // toggle and priority sort). The main search bar DOES apply here too —
  // finding a campaign ticket by the contact's email/company is exactly
  // what reps need most, since these tickets often carry a generic
  // auto-generated title. Kept as its own fetch + effect (see below) so
  // its filters (campaignTypeFilter/campaignFilter/campaignPage) never
  // refire the five status-count requests or the main list above, and
  // vice versa.
  const fetchCampaignTicketData = useCallback(async (): Promise<{
    campaignTickets: { items: API.TicketViewItem[]; total: number };
    campaignTypeCounts: Record<CampaignTicketTypeFilter, number>;
  }> => {
    const campaignOptions: TicketViewListOptions = {
      // The Engaged/Bounced/Never-opened toggle exists to let a rep focus
      // one queue at a time, but a search is a rep looking for one specific
      // record they already know about — the toggle silently scoping search
      // results to whichever type happened to be selected caused a rep to
      // search for a ticket he was actively working, get zero results
      // because it was e.g. a "Bounced" ticket while "Engaged" was selected,
      // and nearly duplicate the work. Search bypasses the toggle entirely,
      // and reaches all four campaign subject types (including "campaign"
      // call-step/activation tickets, normally only reachable via the
      // campaign filter dropdown) — not just the toggle's three — so search
      // really does mean "search everything on this page", not "search
      // everything the toggle offers".
      subjectType: searchQuery
        ? CAMPAIGN_TICKET_SUBJECT_TYPES
        : CAMPAIGN_TICKET_TYPE_SUBJECT_TYPES[campaignTypeFilter],
      status: filterStatus.length ? filterStatus : DEFAULT_VISIBLE_STATUSES,
      sort: CAMPAIGN_SORT_FIELD_TO_PARAM[campaignSortBy],
      pageSize: CAMPAIGN_TICKETS_PAGE_SIZE,
      page: campaignPage,
      // Untouched only — a rep-actioned ticket moved to its own "Actioned"
      // box below (see fetchActionedCampaignTicketData) instead of showing
      // in both.
      actioned: false,
    };
    if (
      campaignFilter !== "all" &&
      !isCampaignlessTicketType(campaignTypeFilter)
    )
      campaignOptions.campaignId = [campaignFilter];
    if (showUrgentOnly) campaignOptions.priority = ["urgent"];
    if (searchQuery) campaignOptions.q = searchQuery;
    if (scope === "mine") {
      campaignOptions.assignedToUserId = currentUserId
        ? [currentUserId]
        : undefined;
    } else if (scope === "needs_cover") {
      campaignOptions.needsCover = true;
    } else if (scope === "department") {
      if (selectedDepartmentSlug)
        campaignOptions.department = [selectedDepartmentSlug];
    } else if (filterAssignee.length) {
      campaignOptions.assignedToUserId = filterAssignee;
    }
    if (filterCustomer.length) campaignOptions.customerId = filterCustomer;

    // One count per toggle for its badge, under the box's other filters as
    // they stand — so each number is what that toggle would show if picked.
    // Landing-page enquiries belong to no campaign, and picking that toggle
    // clears the campaign dropdown, so its count ignores the dropdown too.
    const countTypeTickets = (type: CampaignTicketTypeFilter) => {
      const countOptions: TicketViewListOptions = {
        ...campaignOptions,
        subjectType: CAMPAIGN_TICKET_TYPE_SUBJECT_TYPES[type],
        page: 1,
        pageSize: 1,
      };
      if (isCampaignlessTicketType(type)) delete countOptions.campaignId;
      else if (campaignFilter !== "all")
        countOptions.campaignId = [campaignFilter];
      return dataAdapter.getTicketViews(countOptions).then((r) => r.total);
    };
    const [campaignResponse, typeTotals] = await Promise.all([
      dataAdapter.getTicketViews(campaignOptions),
      Promise.all(
        CAMPAIGN_TICKET_TYPE_OPTIONS.map((o) => countTypeTickets(o.key)),
      ),
    ]);
    const campaignTypeCounts = Object.fromEntries(
      CAMPAIGN_TICKET_TYPE_OPTIONS.map((o, i) => [o.key, typeTotals[i]]),
    ) as Record<CampaignTicketTypeFilter, number>;
    return {
      campaignTickets: {
        items: campaignResponse.items,
        total: campaignResponse.total,
      },
      campaignTypeCounts,
    };
  }, [
    dataAdapter,
    scope,
    selectedDepartmentSlug,
    filterCustomer,
    filterStatus,
    filterAssignee,
    campaignTypeFilter,
    campaignFilter,
    campaignPage,
    currentUserId,
    showUrgentOnly,
    searchQuery,
    campaignSortBy,
  ]);

  // CSV export of outstanding campaign tickets — "All Tickets" scope only,
  // so it covers every rep by default (marketing needs to see and
  // reallocate tickets that are stalling across the whole team, not just
  // their own). Deliberately omits the untouched/actioned split the two
  // boxes above use: an export is for reviewing what's still outstanding
  // regardless of whether a rep has started it. Still respects whichever
  // campaign/status/assignee filters are currently set, and the search box,
  // so "export what I'm looking at" holds for this box the same way it
  // does for the others.
  const handleExportCampaignTicketsCsv = useCallback(async () => {
    setIsExportingCampaignTickets(true);
    try {
      const params = new URLSearchParams();
      for (const t of CAMPAIGN_TICKET_SUBJECT_TYPES)
        params.append("subject_type", t);
      for (const s of filterStatus.length
        ? filterStatus
        : DEFAULT_VISIBLE_STATUSES)
        params.append("status", s);
      if (campaignFilter !== "all")
        params.append("campaign_id", campaignFilter);
      if (filterAssignee.length)
        for (const a of filterAssignee) params.append("assigned_to_user_id", a);
      if (filterCustomer.length)
        for (const c of filterCustomer) params.append("customer_id", c);
      if (searchQuery) params.set("q", searchQuery);
      const url = `${joinApiUrl(getApiBaseUrl(), "views/tickets/export.csv")}?${params.toString()}`;
      const token = await getAuthToken();
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`Export failed: ${resp.status}`);
      const csv = await resp.text();
      capabilities.downloadTextFile("marketing-tickets.csv", csv, "text/csv");
    } catch (err) {
      console.error("Campaign tickets CSV export failed:", err);
    } finally {
      setIsExportingCampaignTickets(false);
    }
  }, [
    filterStatus,
    campaignFilter,
    filterAssignee,
    filterCustomer,
    searchQuery,
    capabilities,
  ]);

  // The "Actioned" box — same scoping/filters as the pink box above, but
  // actioned:true instead of false, and its own page cursor. Kept as a
  // separate fetch (rather than a client-side split of one response) so
  // each box's own pagination total is correct.
  const fetchActionedCampaignTicketData = useCallback(async (): Promise<{
    actionedCampaignTickets: { items: API.TicketViewItem[]; total: number };
  }> => {
    const actionedOptions: TicketViewListOptions = {
      // See the matching comment in fetchCampaignTicketData — search bypasses
      // the type toggle here too, for the same reason, and reaches all four
      // campaign subject types rather than just the toggle's three.
      subjectType: searchQuery
        ? CAMPAIGN_TICKET_SUBJECT_TYPES
        : CAMPAIGN_TICKET_TYPE_SUBJECT_TYPES[campaignTypeFilter],
      status: filterStatus.length ? filterStatus : DEFAULT_VISIBLE_STATUSES,
      sort: CAMPAIGN_SORT_FIELD_TO_PARAM[campaignSortBy],
      pageSize: CAMPAIGN_TICKETS_PAGE_SIZE,
      page: actionedCampaignPage,
      actioned: true,
    };
    if (
      campaignFilter !== "all" &&
      !isCampaignlessTicketType(campaignTypeFilter)
    )
      actionedOptions.campaignId = [campaignFilter];
    if (showUrgentOnly) actionedOptions.priority = ["urgent"];
    if (searchQuery) actionedOptions.q = searchQuery;
    if (scope === "mine") {
      actionedOptions.assignedToUserId = currentUserId
        ? [currentUserId]
        : undefined;
    } else if (scope === "needs_cover") {
      actionedOptions.needsCover = true;
    } else if (scope === "department") {
      if (selectedDepartmentSlug)
        actionedOptions.department = [selectedDepartmentSlug];
    } else if (filterAssignee.length) {
      actionedOptions.assignedToUserId = filterAssignee;
    }
    if (filterCustomer.length) actionedOptions.customerId = filterCustomer;

    const actionedResponse = await dataAdapter.getTicketViews(actionedOptions);
    return {
      actionedCampaignTickets: {
        items: actionedResponse.items,
        total: actionedResponse.total,
      },
    };
  }, [
    dataAdapter,
    scope,
    selectedDepartmentSlug,
    filterCustomer,
    filterStatus,
    filterAssignee,
    campaignTypeFilter,
    campaignFilter,
    actionedCampaignPage,
    currentUserId,
    showUrgentOnly,
    searchQuery,
    campaignSortBy,
  ]);

  // Refetch the main list (status badge counts + current filter selection)
  // whenever the filters/sort/search/page state it depends on changes.
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    fetchMainTicketData()
      .then(({ ticketsByStatus, currentView }) => {
        if (cancelled) return;
        setTicketsByStatus(ticketsByStatus);
        setCurrentView(currentView);
      })
      .catch((error) => {
        console.error("Error loading tickets:", error);
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUserId, fetchMainTicketData]);

  // Refetch the pinned campaign-tickets box independently of the main list —
  // see fetchCampaignTicketData for why this is split out.
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    setCampaignLoading(true);
    // The visible set is about to change under this filter — drop any
    // selection made against the previous set rather than let it point at
    // now-offscreen tickets.
    setSelectedCampaignTicketIds(new Set());
    fetchCampaignTicketData()
      .then(({ campaignTickets, campaignTypeCounts }) => {
        if (cancelled) return;
        setCampaignTickets(campaignTickets);
        setCampaignTypeCounts(campaignTypeCounts);
        // Same reasoning as the "Actioned" box's auto-expand below — this
        // box is also collapsed by default, and it holds the *untouched*
        // tickets, the more likely home of a ticket a rep has started and is
        // hunting for (Gary's report). A search match hidden behind the
        // collapsed header is exactly the same failure the type-toggle
        // bypass exists to fix.
        if (searchQuery && campaignTickets.total > 0) {
          setCampaignSectionExpanded(true);
        }
      })
      .catch((error) => {
        console.error("Error loading campaign tickets:", error);
      })
      .finally(() => {
        if (!cancelled) setCampaignLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUserId, fetchCampaignTicketData, searchQuery]);

  // Refetch the "Actioned" box independently, same shape as the pink box's
  // effect above.
  useEffect(() => {
    if (!currentUserId) return;
    let cancelled = false;
    setActionedCampaignLoading(true);
    fetchActionedCampaignTicketData()
      .then(({ actionedCampaignTickets }) => {
        if (cancelled) return;
        setActionedCampaignTickets(actionedCampaignTickets);
        // A search match living in the collapsed "Actioned" box is just as
        // invisible as one scoped out by the type toggle above — auto-expand
        // so a search actually surfaces it instead of the rep having to know
        // to open this box. Only ever expands, never collapses, so clearing
        // the search leaves whatever open/closed state the rep left it in.
        if (searchQuery && actionedCampaignTickets.total > 0) {
          setActionedSectionExpanded(true);
        }
      })
      .catch((error) => {
        console.error("Error loading actioned campaign tickets:", error);
      })
      .finally(() => {
        if (!cancelled) setActionedCampaignLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUserId, fetchActionedCampaignTicketData, searchQuery]);

  const refreshTickets = async () => {
    if (!currentUserId) return;
    const [
      { ticketsByStatus, currentView },
      { campaignTickets, campaignTypeCounts },
      { actionedCampaignTickets },
    ] = await Promise.all([
      fetchMainTicketData(),
      fetchCampaignTicketData(),
      fetchActionedCampaignTicketData(),
    ]);
    setTicketsByStatus(ticketsByStatus);
    setCurrentView(currentView);
    setCampaignTickets(campaignTickets);
    setCampaignTypeCounts(campaignTypeCounts);
    setActionedCampaignTickets(actionedCampaignTickets);
  };

  const handleTicketComplete = async (ticketId: string) => {
    try {
      // "Mark as Resolved" resolves the ticket — the requester/watchers are
      // notified, and the scheduled sweep auto-closes it after a quiet
      // period unless someone reopens it first.
      await dataAdapter.updateTicket(ticketId, { status: "resolved" });
      notifyTicketsChanged();
      await refreshTickets();
    } catch (error) {
      console.error("Error completing ticket:", error);
    }
  };

  const handleTicketReopen = async (ticketId: string) => {
    try {
      await dataAdapter.reopenTicket(ticketId);
      notifyTicketsChanged();
      await refreshTickets();
    } catch (error) {
      console.error("Error reopening ticket:", error);
    }
  };

  const handleSaveTicket = async (
    ticketId: string,
    updates: API.TicketUpdateRequest,
  ) => {
    try {
      await dataAdapter.updateTicket(ticketId, updates);
      // updates may include a status/priority/category change (metadata
      // strip edits), so keep the nav badge and notification bell honest.
      notifyTicketsChanged();
      await refreshTickets();
    } catch (error) {
      console.error("Error updating ticket:", error);
    }
  };

  const handleAddTicket = async (data: {
    title: string;
    description: string;
    actionType: "call" | "email" | "meeting" | "general";
    dueDate: string;
    priority: "low" | "normal" | "high" | "urgent";
    categoryId: string | null;
    customerId: string | null;
    assignedToUserId: string | null;
    departmentSlug: string | null;
    customerPersonId: string | null;
    subjectType: "alert" | "opportunity" | "job" | "estimate" | null;
    subjectId: string | null;
    subjectLabel: string | null;
  }) => {
    setIsSubmittingTicket(true);
    try {
      // assignedToUserId omitted (null) -> assigned_to_user_id undefined ->
      // the backend assigns to the creator. A department-routed ticket
      // must NOT fall into that default: it sends assigned_to_user_id as an
      // explicit null (not omitted) so the ticket stays unassigned and the
      // department resolution in create_ticket (INV-1) actually takes
      // ownership, rather than the creator silently swallowing it.
      await dataAdapter.createTicket({
        title: data.title,
        description: data.description || undefined,
        due_date: data.dueDate || undefined,
        action_type: data.actionType,
        priority: data.priority,
        category_id: data.categoryId ?? undefined,
        customer_id: data.customerId ?? undefined,
        assigned_to_user_id: data.departmentSlug
          ? null
          : (data.assignedToUserId ?? undefined),
        department_slug: data.departmentSlug ?? undefined,
        customer_person_id: data.customerPersonId ?? undefined,
        subject_type: data.subjectType ?? undefined,
        subject_id: data.subjectId ?? undefined,
        subject_label: data.subjectLabel ?? undefined,
      });

      setShowAddTicket(false);
      // Sort so the newest ticket is visible at the top.
      setSortBy("created_at");
      await refreshTickets();
    } catch (error) {
      console.error("Error creating ticket:", error);
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  // Build the customer-filter dropdown from whichever ticket views are
  // currently loaded. This is a UI affordance derived from the current
  // visible set — it's NOT a client-side filter of list results, just
  // a list of filter candidates. Customer name is already inline.
  const ticketCustomers = Array.from(
    new Map(
      STATUS_OPTIONS.flatMap((tab) => ticketsByStatus[tab.key].items)
        .filter((t) => t.customer_id && t.customer_name)
        .map((t) => [t.customer_id as string, t.customer_name as string]),
    ).entries(),
  );

  const activeFilterCount = [
    filterStatus.length > 0,
    filterActionType.length > 0,
    filterCustomer.length > 0,
    filterCategory.length > 0,
    filterPriority.length > 0,
    filterSource.length > 0,
    filterAssignee.length > 0,
  ].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0;

  const clearFilters = () => {
    setFilterStatus([]);
    setFilterActionType([]);
    setFilterCustomer([]);
    setFilterCategory([]);
    setFilterPriority([]);
    setFilterSource([]);
    setFilterAssignee([]);
  };

  // The main list drops optional columns no ticket on the page fills, so the
  // title keeps its room; the compact campaign box has its own column set.
  const mainListColumns = ticketListColumns(currentView.items);
  const renderTicketCard = (ticket: API.TicketViewItem, compact = false) => (
    <TicketCard
      key={ticket.id}
      ticket={ticket}
      customerName={ticket.customer_name ?? undefined}
      compact={compact}
      columns={compact ? undefined : mainListColumns}
      onComplete={() => handleTicketComplete(ticket.id)}
      onReopen={() => handleTicketReopen(ticket.id)}
      onSaveTicket={(updates) => handleSaveTicket(ticket.id, updates)}
    />
  );
  // The pink box drops the covering/category columns, which are near-always
  // empty for campaign-raised tickets (no CSR-coverage or category concept
  // applies to them) — see TicketCard's `compact` prop. Also carries a
  // checkbox for the box's own multi-select bulk-close/reassign (see that
  // section below) — deliberately not added to the main list's cards.
  const renderCampaignTicketCard = (ticket: API.TicketViewItem) => (
    <div key={ticket.id} className="flex items-start gap-2">
      <input
        type="checkbox"
        className="mt-3 size-3.5 rounded border-pink-300 dark:border-pink-800 shrink-0"
        checked={selectedCampaignTicketIds.has(ticket.id)}
        onChange={(e) =>
          setSelectedCampaignTicketIds((prev) => {
            const next = new Set(prev);
            if (e.target.checked) next.add(ticket.id);
            else next.delete(ticket.id);
            return next;
          })
        }
        aria-label={`Select ${ticket.title} for bulk close or reassign`}
      />
      <div className="flex-1 min-w-0">{renderTicketCard(ticket, true)}</div>
    </div>
  );

  const handleBulkCloseCampaignTickets = async () => {
    const ticketIds = Array.from(selectedCampaignTicketIds);
    if (ticketIds.length === 0 || !bulkCloseNote.trim()) return;
    setBulkClosing(true);
    setBulkCloseSkipped([]);
    try {
      const result = await dataAdapter.bulkCloseTickets({
        ticket_ids: ticketIds,
        note_text: bulkCloseNote.trim(),
      });
      setSelectedCampaignTicketIds(new Set());
      setBulkCloseNote("");
      setBulkCloseSkipped(result.skipped);
      notifyTicketsChanged();
      await refreshTickets();
    } catch (error) {
      console.error("Error bulk-closing tickets:", error);
    } finally {
      setBulkClosing(false);
    }
  };

  const handleBulkReassignCampaignTickets = async () => {
    const ticketIds = Array.from(selectedCampaignTicketIds);
    if (ticketIds.length === 0 || !bulkReassignToUserId) return;
    setBulkReassigning(true);
    setBulkReassignSkipped([]);
    try {
      const result = await dataAdapter.bulkDelegateTickets({
        ticket_ids: ticketIds,
        to_user_id: bulkReassignToUserId,
      });
      setSelectedCampaignTicketIds(new Set());
      setBulkReassignToUserId("");
      setBulkReassignSkipped(result.skipped);
      notifyTicketsChanged();
      await refreshTickets();
    } catch (error) {
      console.error("Error bulk-reassigning tickets:", error);
    } finally {
      setBulkReassigning(false);
    }
  };

  const handleDismissCustomerScope = () => {
    setFilterCustomer([]);
    const next = new URLSearchParams(searchParams);
    next.delete("customer");
    setSearchParams(next, { replace: true });
  };

  // Gate for the whole campaign-tickets area (both the pink "untouched" box
  // and the "Actioned" box below it) — hidden entirely only when there's
  // nothing to show under the default view, same rule as before the
  // untouched/actioned split. Every campaign ticket is system generated, so
  // the area hides when the Source filter asks for person-set tickets only.
  const isPersonSetOnly =
    filterSource.length === 1 && filterSource[0] === "person";
  const campaignSectionVisible =
    !isPersonSetOnly &&
    (campaignLoading ||
      actionedCampaignLoading ||
      campaignTickets.total > 0 ||
      actionedCampaignTickets.total > 0 ||
      campaignTypeFilter !== "campaign_contact" ||
      campaignFilter !== "all" ||
      showUrgentOnly ||
      !!searchQuery);

  // department-ticket-routing: the department currently selected under the
  // "department" scope, out of everything myDepartments fetched this user
  // belongs to.
  const activeDepartment = myDepartments.find(
    (d) => d.slug === selectedDepartmentSlug,
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl mb-2">
            {urlCustomer
              ? `Tickets for ${customerName ?? "…"}`
              : scope === "mine"
                ? "My Tickets"
                : scope === "needs_cover"
                  ? "Needs Cover"
                  : scope === "department"
                    ? (activeDepartment?.name ?? "Department")
                    : "All Tickets"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {urlCustomer
              ? "Showing tickets for the selected customer"
              : scope === "mine"
                ? "Your work surface - manage and track all your tickets"
                : scope === "needs_cover"
                  ? "Tickets whose CSR/AM is away today and nobody's been assigned to cover yet"
                  : scope === "department"
                    ? "Tickets routed here because they couldn't reach an individual — claim one to take it on"
                    : "Every ticket you have visibility into, regardless of assignee"}
          </p>
        </div>
        <Button onClick={() => setShowAddTicket(true)}>
          <Plus className="size-4 mr-2" />
          New Ticket
        </Button>
      </div>

      {/* Scope toggle */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/30 w-fit">
          {(["mine", "all", "needs_cover"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                scope === s
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {s === "mine"
                ? "My Tickets"
                : s === "needs_cover"
                  ? "Needs Cover"
                  : "All Tickets"}
            </button>
          ))}
          {/* Absent entirely for a user in no department — the overwhelming
              majority — per the frontend update spec §4 acceptance bar. One
              tab either way (never one per department): a multi-site member
              gets the picker below instead of N tabs. */}
          {myDepartments.length > 0 && (
            <button
              onClick={() => setScope("department")}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                scope === "department"
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {myDepartments.length === 1
                ? myDepartments[0]!.name
                : (activeDepartment?.name ?? "Department")}
            </button>
          )}
        </div>
        {/* Multi-site staff (e.g. Prepress - Exeter and Prepress -
            Buckingham) pick which of their own department inboxes to view,
            rather than getting a tab per department. Full department name,
            not the bare function, per spec §4. */}
        {scope === "department" && myDepartments.length > 1 && (
          <select
            value={selectedDepartmentSlug}
            onChange={(e) => setSelectedDepartmentSlug(e.target.value)}
            className="px-2.5 py-1.5 border border-border rounded-md bg-background text-sm"
            aria-label="Choose department"
          >
            {myDepartments.map((d) => (
              <option key={d.slug} value={d.slug}>
                {d.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Active scope chips */}
      {urlCustomer && (
        <div className="flex items-center gap-2 flex-wrap">
          <CustomerScopeChip
            customerName={customerName}
            onDismiss={handleDismissCustomerScope}
          />
        </div>
      )}

      {/* Sort and Filter Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search tickets..."
            className="pl-8 h-9 w-[220px] text-sm"
          />
        </div>

        {/* Sort */}
        <div className="flex items-center gap-2">
          <ArrowUpDown className="size-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">Sort by:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortField)}
            className="px-3 py-1.5 border border-border rounded bg-background text-sm"
          >
            <option value="due_date">Due Date</option>
            <option value="created_at">Created Date</option>
            <option value="title">Title (A-Z)</option>
            <option value="priority">Priority</option>
            <option value="tags">Tag</option>
          </select>
        </div>

        {/* Filters — status (previously a separate tab bar) plus every
            other ticket filter, consolidated into one slide-over panel. */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setFiltersOpen(true)}
        >
          <Filter className="size-4 mr-2" />
          {activeFilterCount > 0
            ? `${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"}`
            : "Filters"}
        </Button>

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Clear filters
          </button>
        )}
      </div>

      <SlideOverPanel
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        title="Filters"
      >
        <div className="space-y-6">
          <FilterCheckboxGroup
            label="Status"
            value={filterStatus}
            onChange={setFilterStatus}
            options={STATUS_OPTIONS.map((tab) => ({
              value: tab.key,
              label: `${tab.label} (${ticketsByStatus[tab.key].total})`,
            }))}
          />

          <FilterCheckboxGroup
            label="Source"
            value={filterSource}
            onChange={setFilterSource}
            options={[
              { value: "system", label: "System generated" },
              { value: "person", label: "Set by a person" },
            ]}
          />

          <FilterCheckboxGroup
            label="Action Type"
            value={filterActionType}
            onChange={setFilterActionType}
            options={[
              { value: "call", label: "Call" },
              { value: "email", label: "Email" },
              { value: "meeting", label: "Meeting" },
              { value: "general", label: "General" },
            ]}
          />

          <FilterCheckboxGroup
            label="Priority"
            value={filterPriority}
            onChange={setFilterPriority}
            options={[
              { value: "urgent", label: "Urgent" },
              { value: "high", label: "High" },
              { value: "normal", label: "Normal" },
              { value: "low", label: "Low" },
            ]}
          />

          <FilterCheckboxGroup
            label="Category"
            value={filterCategory}
            onChange={setFilterCategory}
            options={categories.map((cat) => ({
              value: cat.id,
              label: cat.name,
            }))}
          />

          <FilterCheckboxGroup
            label="Customer"
            value={filterCustomer}
            onChange={setFilterCustomer}
            options={ticketCustomers.map(([id, name]) => ({
              value: id,
              label: name,
            }))}
          />

          {/* Assignee filter — only meaningful in "All Tickets" scope;
              "My Tickets" already scopes to the current user. */}
          {scope === "all" && (
            <FilterCheckboxGroup
              label="Assigned To"
              value={filterAssignee}
              onChange={setFilterAssignee}
              options={assignableUsers.map((u) => ({
                value: u.id,
                label: u.name,
              }))}
            />
          )}
        </div>
      </SlideOverPanel>

      {/* Pinned "look here at these" section for marketing-campaign tickets —
          always visible above the main list (never hidden by pagination
          under the much higher-volume account_health/at-risk auto-tickets),
          scoped by who/which customer like the rest of the page but not by
          the ad-hoc action-type/category/priority filters (search does
          apply). Hidden entirely only when there's nothing to show under the
          default view (Engaged, no campaign/urgent filter, no search) — but
          a non-default toggle selection or an active search with zero
          results still renders (with the toggle) so switching to another
          type or clearing the filter doesn't require reloading the page. */}
      {campaignSectionVisible && (
        <div className="space-y-3">
          {/* Type toggle and campaign filter both reissue the request
                scoped server-side rather than filtering the already-fetched
                page client-side (see the list-endpoint comment at the top
                of this file) — and both combine (AND) when set together.
                Shared by both boxes below (untouched and actioned) so
                toggling here refires both fetches in step, rather than
                each box carrying its own copy of the same controls. */}
          {/* Hidden while the pink box is collapsed — otherwise these read
              as if they apply to the main ticket list above, which they
              don't (see the campaignSectionVisible gate for why the box
              itself can still be present but collapsed). */}
          {campaignSectionExpanded && (
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-lg border border-pink-200 dark:border-pink-800 p-0.5 bg-background/60 w-fit">
                {CAMPAIGN_TICKET_TYPE_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setCampaignTypeFilter(option.key);
                      // Visibly clear it rather than quietly ignoring it —
                      // a dropdown still reading "Advent 2026" while the
                      // fetch ignores it is the kind of lie that costs an
                      // afternoon.
                      if (isCampaignlessTicketType(option.key))
                        setCampaignFilter("all");
                    }}
                    title={
                      campaignTypeCounts[option.key] > 0
                        ? `${campaignTypeCounts[option.key]} to action`
                        : undefined
                    }
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs transition-colors ${
                      campaignTypeFilter === option.key
                        ? "bg-pink-100 dark:bg-pink-900/50 text-pink-900 dark:text-pink-100 shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {option.label}
                    {campaignTypeCounts[option.key] > 0 && (
                      // Hidden from the accessible name (it stays the bare
                      // label); the count reaches assistive tech as the
                      // button's title/description instead.
                      <span
                        className="text-[10px] leading-none bg-red-500 text-white px-1.5 py-0.5 rounded-full"
                        aria-hidden="true"
                      >
                        {campaignTypeCounts[option.key]}
                      </span>
                    )}
                  </button>
                ))}
              </div>
              {searchQuery && (
                <span className="text-xs text-muted-foreground">
                  Searching all types
                </span>
              )}
              <select
                value={campaignFilter}
                onChange={(e) => setCampaignFilter(e.target.value)}
                disabled={isCampaignlessTicketType(campaignTypeFilter)}
                title={
                  isCampaignlessTicketType(campaignTypeFilter)
                    ? "Landing-page enquiries don't belong to a campaign"
                    : undefined
                }
                className="px-2.5 py-1 border border-pink-200 dark:border-pink-800 rounded-md bg-background/60 text-xs disabled:opacity-50"
              >
                <option value="all">All campaigns</option>
                {availableCampaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <select
                value={campaignSortBy}
                onChange={(e) =>
                  setCampaignSortBy(e.target.value as CampaignSortField)
                }
                className="px-2.5 py-1 border border-pink-200 dark:border-pink-800 rounded-md bg-background/60 text-xs"
                aria-label="Sort campaign tickets"
              >
                <option value="created_at">Newest first</option>
                <option value="priority">Priority</option>
              </select>
              <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground px-1">
                <input
                  type="checkbox"
                  checked={showUrgentOnly}
                  onChange={(e) => setShowUrgentOnly(e.target.checked)}
                  className="size-3.5 rounded border-pink-300 dark:border-pink-800"
                />
                Show urgent only
              </label>
              {scope === "all" && (
                <button
                  type="button"
                  onClick={handleExportCampaignTicketsCsv}
                  disabled={isExportingCampaignTickets}
                  className="ml-auto flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border border-pink-200 dark:border-pink-800 bg-background/60 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Download className="size-3.5" />
                  {isExportingCampaignTickets ? "Exporting…" : "Export CSV"}
                </button>
              )}
            </div>
          )}

          {/* Pinned "look here at these" box for untouched marketing-campaign
              tickets — always visible above the main list (never hidden by
              pagination under the much higher-volume account_health/at-risk
              auto-tickets), scoped by who/which customer like the rest of the
              page but not by the ad-hoc action-type/category/priority
              filters (search does apply). */}
          <Collapsible
            open={campaignSectionExpanded}
            onOpenChange={setCampaignSectionExpanded}
            className="border border-pink-300 dark:border-pink-800 rounded-lg bg-pink-50/50 dark:bg-pink-950/20"
          >
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg"
              >
                <ChevronDown
                  className={`size-4 text-muted-foreground transition-transform ${
                    campaignSectionExpanded ? "" : "-rotate-90"
                  }`}
                />
                <Megaphone className="size-4 text-pink-600 dark:text-pink-400" />
                <span className="font-medium">Campaign tickets</span>
                <span className="text-sm text-muted-foreground">
                  · {campaignTickets.total} to action
                </span>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent className="px-4 pb-4 space-y-3">
              {selectedCampaignTicketIds.size > 0 && (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-pink-200 dark:border-pink-800 bg-background/60 px-3 py-2">
                  <span className="text-xs font-medium whitespace-nowrap">
                    {selectedCampaignTicketIds.size} selected
                  </span>
                  <input
                    type="text"
                    value={bulkCloseNote}
                    onChange={(e) => setBulkCloseNote(e.target.value)}
                    placeholder='Note for every closed ticket, e.g. "Closed, dealing with Gerald"'
                    className="flex-1 min-w-[12rem] px-2.5 py-1 border border-pink-200 dark:border-pink-800 rounded-md bg-background text-xs"
                  />
                  <Button
                    size="sm"
                    disabled={bulkClosing || !bulkCloseNote.trim()}
                    onClick={handleBulkCloseCampaignTickets}
                  >
                    {bulkClosing
                      ? "Closing…"
                      : `Close ${selectedCampaignTicketIds.size} ticket${selectedCampaignTicketIds.size === 1 ? "" : "s"}`}
                  </Button>
                  <span className="text-xs text-muted-foreground">or</span>
                  <select
                    value={bulkReassignToUserId}
                    onChange={(e) => setBulkReassignToUserId(e.target.value)}
                    className="px-2.5 py-1 border border-pink-200 dark:border-pink-800 rounded-md bg-background text-xs"
                    aria-label="Reassign selected tickets to"
                  >
                    <option value="">Reassign to…</option>
                    {assignableUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    disabled={bulkReassigning || !bulkReassignToUserId}
                    onClick={handleBulkReassignCampaignTickets}
                  >
                    {bulkReassigning
                      ? "Reassigning…"
                      : `Reassign ${selectedCampaignTicketIds.size} ticket${selectedCampaignTicketIds.size === 1 ? "" : "s"}`}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={bulkClosing || bulkReassigning}
                    onClick={() => setSelectedCampaignTicketIds(new Set())}
                  >
                    Clear
                  </Button>
                </div>
              )}
              {bulkCloseSkipped.length > 0 && (
                <p className="text-xs text-red-600">
                  {bulkCloseSkipped.length} ticket
                  {bulkCloseSkipped.length === 1 ? "" : "s"} didn't close (
                  {bulkCloseSkipped.map((s) => s.reason).join("; ")}).
                </p>
              )}
              {bulkReassignSkipped.length > 0 && (
                <p className="text-xs text-red-600">
                  {bulkReassignSkipped.length} ticket
                  {bulkReassignSkipped.length === 1 ? "" : "s"} didn't reassign
                  ({bulkReassignSkipped.map((s) => s.reason).join("; ")}).
                </p>
              )}
              {campaignLoading ? (
                <div className="text-sm text-muted-foreground py-4 text-center">
                  Loading campaign tickets...
                </div>
              ) : campaignTickets.total === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No{" "}
                  {
                    CAMPAIGN_TICKET_TYPE_OPTIONS.find(
                      (o) => o.key === campaignTypeFilter,
                    )?.emptyNoun
                  }{" "}
                  right now.
                </p>
              ) : (
                <div className="space-y-2 overflow-x-auto">
                  {campaignTickets.items.map(renderCampaignTicketCard)}
                </div>
              )}
              {/* Pagination — its own page state, independent of the main
                  list below, so scrolling this box no longer risks scrolling
                  the whole page (the previous scroll-container approach). */}
              {!campaignLoading &&
                campaignTickets.total > CAMPAIGN_TICKETS_PAGE_SIZE && (
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                      {(campaignPage - 1) * CAMPAIGN_TICKETS_PAGE_SIZE + 1}–
                      {Math.min(
                        campaignPage * CAMPAIGN_TICKETS_PAGE_SIZE,
                        campaignTickets.total,
                      )}{" "}
                      of {campaignTickets.total}
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCampaignPage((p) => Math.max(1, p - 1))
                        }
                        disabled={campaignPage <= 1}
                      >
                        <ChevronLeft className="size-4" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCampaignPage((p) => p + 1)}
                        disabled={
                          campaignPage * CAMPAIGN_TICKETS_PAGE_SIZE >=
                          campaignTickets.total
                        }
                      >
                        Next
                        <ChevronRight className="size-4" />
                      </Button>
                    </div>
                  </div>
                )}
            </CollapsibleContent>
          </Collapsible>

          {/* "Actioned" box — same campaign tickets, once a rep has left a
              human-authored activity row on them (comment, status change,
              reassignment, ...). Collapsed by default and visually muted
              (slate, not pink) — it's a "what's already being worked"
              reference, not the primary call-to-action, so it shouldn't
              compete with the untouched box above for attention. No
              bulk-close controls here — these tickets are already being
              worked, so bulk-closing isn't the action a rep needs from this
              box. Bulk-reassign *is* offered here though (sharing the same
              selection set as the pink box above) — this is exactly the
              set a manager reviewing a stalling-tickets export needs to
              reallocate, since "already being worked but stalling" is the
              whole point of that export. */}
          {(actionedCampaignLoading || actionedCampaignTickets.total > 0) && (
            <Collapsible
              open={actionedSectionExpanded}
              onOpenChange={setActionedSectionExpanded}
              className="border border-border rounded-lg bg-muted/30"
            >
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-lg"
                >
                  <ChevronDown
                    className={`size-4 text-muted-foreground transition-transform ${
                      actionedSectionExpanded ? "" : "-rotate-90"
                    }`}
                  />
                  <Megaphone className="size-4 text-muted-foreground" />
                  <span className="font-medium">
                    Actioned - campaign tickets
                  </span>
                  <span className="text-sm text-muted-foreground">
                    · {actionedCampaignTickets.total} being worked
                  </span>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="px-4 pb-4 space-y-3">
                {selectedCampaignTicketIds.size > 0 && (
                  <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-background/60 px-3 py-2">
                    <span className="text-xs font-medium whitespace-nowrap">
                      {selectedCampaignTicketIds.size} selected
                    </span>
                    <select
                      value={bulkReassignToUserId}
                      onChange={(e) => setBulkReassignToUserId(e.target.value)}
                      className="px-2.5 py-1 border border-border rounded-md bg-background text-xs"
                      aria-label="Reassign selected tickets to"
                    >
                      <option value="">Reassign to…</option>
                      {assignableUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      disabled={bulkReassigning || !bulkReassignToUserId}
                      onClick={handleBulkReassignCampaignTickets}
                    >
                      {bulkReassigning
                        ? "Reassigning…"
                        : `Reassign ${selectedCampaignTicketIds.size} ticket${selectedCampaignTicketIds.size === 1 ? "" : "s"}`}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={bulkReassigning}
                      onClick={() => setSelectedCampaignTicketIds(new Set())}
                    >
                      Clear
                    </Button>
                  </div>
                )}
                {bulkReassignSkipped.length > 0 && (
                  <p className="text-xs text-red-600">
                    {bulkReassignSkipped.length} ticket
                    {bulkReassignSkipped.length === 1 ? "" : "s"} didn't
                    reassign (
                    {bulkReassignSkipped.map((s) => s.reason).join("; ")}).
                  </p>
                )}
                {actionedCampaignLoading ? (
                  <div className="text-sm text-muted-foreground py-4 text-center">
                    Loading actioned tickets...
                  </div>
                ) : (
                  <div className="space-y-2 overflow-x-auto">
                    {actionedCampaignTickets.items.map((ticket) =>
                      renderCampaignTicketCard(ticket),
                    )}
                  </div>
                )}
                {/* Pagination — its own page state, independent of the
                    pink box's and the main list's. */}
                {!actionedCampaignLoading &&
                  actionedCampaignTickets.total >
                    CAMPAIGN_TICKETS_PAGE_SIZE && (
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>
                        {(actionedCampaignPage - 1) *
                          CAMPAIGN_TICKETS_PAGE_SIZE +
                          1}
                        –
                        {Math.min(
                          actionedCampaignPage * CAMPAIGN_TICKETS_PAGE_SIZE,
                          actionedCampaignTickets.total,
                        )}{" "}
                        of {actionedCampaignTickets.total}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setActionedCampaignPage((p) => Math.max(1, p - 1))
                          }
                          disabled={actionedCampaignPage <= 1}
                        >
                          <ChevronLeft className="size-4" />
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setActionedCampaignPage((p) => p + 1)}
                          disabled={
                            actionedCampaignPage * CAMPAIGN_TICKETS_PAGE_SIZE >=
                            actionedCampaignTickets.total
                          }
                        >
                          Next
                          <ChevronRight className="size-4" />
                        </Button>
                      </div>
                    </div>
                  )}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      )}

      {/* Tickets Content — loading is scoped to this section only, not the
          whole page, so the header/toolbar/filters panel stay mounted
          across refetches (unmounting the panel here would replay its
          slide-in animation every time a filter changes). */}
      <div className="space-y-2 overflow-x-auto">
        {loading ? (
          <div className="text-muted-foreground text-sm p-8 text-center border border-border rounded-lg">
            Loading tickets...
          </div>
        ) : loadError ? (
          <div className="text-destructive text-sm p-8 text-center border border-border rounded-lg">
            Couldn't load tickets. Try refreshing the page.
          </div>
        ) : currentView.items.length === 0 ? (
          <div className="text-muted-foreground text-sm p-8 text-center border border-border rounded-lg">
            {currentView.total === 0
              ? scope === "department"
                ? // The whole point of the feature working — word the
                  // empty department inbox as the success state it is, not
                  // as a failure or a bare blank (frontend update spec §6).
                  "Nothing unclaimed — great work!"
                : filterStatus.length === 1
                  ? EMPTY_STATUS_MESSAGE[filterStatus[0]]
                  : "No tickets to show"
              : "No tickets match your filters"}
          </div>
        ) : (
          currentView.items.map((ticket) => renderTicketCard(ticket))
        )}
      </div>

      {/* Pagination — driven by currentView, whichever filter combination
          produced it. */}
      {currentView.total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {(page - 1) * PAGE_SIZE + 1}–
            {Math.min(page * PAGE_SIZE, currentView.total)} of{" "}
            {currentView.total}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="size-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page * PAGE_SIZE >= currentView.total}
            >
              Next
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Add Ticket Dialog */}
      {showAddTicket && (
        <TicketModal
          onClose={() => setShowAddTicket(false)}
          onSave={handleAddTicket}
          isSubmitting={isSubmittingTicket}
        />
      )}
    </div>
  );
}
